<template>
  <span
    v-if="recipientClasses[recipient]"
    :class="[
      'inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium',
      recipientClasses[recipient],
    ]"
  >
    <span class="relative flex h-3.5 w-3.5 items-center justify-center">
      <component 
        :is="recipientIcons[recipient]" 
        class="h-3 w-3" 
      />
      
      <span
        :class="[
          'relative inline-flex h-2 w-2 rounded-full',
          recipientClasses[recipient].split(' ')[0],
        ]"
        class="hidden" 
      ></span>
    </span>
    {{ recipientLabels[recipient] }}
  </span>
</template>

<script setup lang="ts">
import { MessageRecipientTypeEnum } from '@/enums/messages/MessageRecipientTypeEnum';
import { 
  Users, CheckCircle, XCircle, Clock, ArrowDown, Repeat, 
  ArrowUp, TrendingDown, Package, ShoppingCart, MessageSquare, 
  GitMerge,
  GitCommit
} from 'lucide-vue-next';

const recipientIcons = {
  [MessageRecipientTypeEnum.ALL]: Users,
  [MessageRecipientTypeEnum.SUBSCRIBERS]: CheckCircle,
  [MessageRecipientTypeEnum.NON_SUBSCRIBERS]: XCircle,
  [MessageRecipientTypeEnum.INACTIVE_SUBSCRIBERS]: Clock,
  [MessageRecipientTypeEnum.DOWNSELL]: ArrowDown,
  [MessageRecipientTypeEnum.RECURRENT]: Repeat,
  [MessageRecipientTypeEnum.UPSELLERS]: ArrowUp,
  [MessageRecipientTypeEnum.DOWNSELLERS]: TrendingDown,
  [MessageRecipientTypeEnum.PACKAGES]: Package,
  [MessageRecipientTypeEnum.ABANDONED_PIX]: ShoppingCart,
  [MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL]: MessageSquare,
  [MessageRecipientTypeEnum.FLOW_NODE]: GitMerge,
  [MessageRecipientTypeEnum.FLOW_CURRENT_NODE]: GitCommit,
  
}

const recipientClasses = {
  [MessageRecipientTypeEnum.ALL]: 'bg-blue-500 text-blue-50 bg-blue-500/10',
  [MessageRecipientTypeEnum.SUBSCRIBERS]: 'bg-emerald-500 text-emerald-50 bg-emerald-500/10',
  [MessageRecipientTypeEnum.NON_SUBSCRIBERS]: 'bg-zinc-500 text-zinc-50 bg-zinc-500/10',
  [MessageRecipientTypeEnum.INACTIVE_SUBSCRIBERS]: 'bg-yellow-500 text-yellow-50 bg-yellow-500/10',
  [MessageRecipientTypeEnum.DOWNSELL]: 'bg-orange-500 text-orange-50 bg-orange-500/10',
  [MessageRecipientTypeEnum.RECURRENT]: 'bg-purple-500 text-purple-50 bg-purple-500/10',
  [MessageRecipientTypeEnum.UPSELLERS]: 'bg-teal-500 text-teal-50 bg-teal-500/10',
  [MessageRecipientTypeEnum.DOWNSELLERS]: 'text-amber-50 bg-amber-500/10',
  [MessageRecipientTypeEnum.PACKAGES]: 'bg-indigo-500 text-indigo-50 bg-indigo-500/10',
  [MessageRecipientTypeEnum.ABANDONED_PIX]: 'bg-red-500 text-red-50 bg-red-500/10',
  [MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL]: 'bg-cyan-500/10 text-cyan-50 bg-cyan-500/10',
  [MessageRecipientTypeEnum.FLOW_NODE]: 'bg-sky-500/50 ring-sky-200 text-white', // Azul mais suave
  [MessageRecipientTypeEnum.FLOW_CURRENT_NODE]: 'bg-blue-500/50 text-white ring-blue-100',
}

const recipientLabels = {
  [MessageRecipientTypeEnum.ALL]: 'Todos',
  [MessageRecipientTypeEnum.SUBSCRIBERS]: 'Assinantes',
  [MessageRecipientTypeEnum.NON_SUBSCRIBERS]: 'Não Assinantes',
  [MessageRecipientTypeEnum.INACTIVE_SUBSCRIBERS]: 'Assinantes Inativos',
  [MessageRecipientTypeEnum.DOWNSELL]: 'Downsell',
  [MessageRecipientTypeEnum.RECURRENT]: 'Recorrentes',
  [MessageRecipientTypeEnum.UPSELLERS]: 'Upsellers',
  [MessageRecipientTypeEnum.DOWNSELLERS]: 'Downsellers',
  [MessageRecipientTypeEnum.PACKAGES]: 'Pacotes',
  [MessageRecipientTypeEnum.ABANDONED_PIX]: 'PIX Abandonado',
  [MessageRecipientTypeEnum.CHAT_JOIN_REQUEST_APPROVAL]: 'Aprovação de Chat',
  [MessageRecipientTypeEnum.FLOW_NODE]: 'Leads que passaram pelo nó',
  [MessageRecipientTypeEnum.FLOW_CURRENT_NODE]: 'Leads que estão no nó',
}

defineProps<{
  recipient: MessageRecipientTypeEnum
}>()
</script>